import { useCallback, useRef, useState } from "react";
import type { ChatMessage } from "@/features/chat/types";
import { gaiaRepository } from "@/features/chat/gaiaRepository";

interface UseGaiaChatOptions {
  /** Shown before the user says anything. */
  greeting?: string;
  /** What the caller is looking at, passed with every message. */
  context?: string;
}

interface GaiaChat {
  messages: ChatMessage[];
  isThinking: boolean;
  error: string | null;
  send: (text: string) => Promise<void>;
}

/**
 * One conversation with GAIA.
 *
 * gaia-app had this logic twice - once in the chat page and again in an unused floating panel.
 * Keeping it in a hook means a contextual panel, when one is wanted, is a different presentation
 * of the same conversation rather than a second implementation that drifts.
 */
export function useGaiaChat({ greeting, context }: UseGaiaChatOptions = {}): GaiaChat {
  const [messages, setMessages] = useState<ChatMessage[]>(() =>
    greeting === undefined ? [] : [{ role: "assistant", content: greeting }],
  );
  const [isThinking, setIsThinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * The conversation, for sequencing.
   *
   * `send` needs the history as it stands at the moment of the call, and awaits between reading it
   * and appending the reply. State alone cannot answer that - the closure would hold a stale
   * array - and reading it inside a state updater puts a side effect somewhere React is free to
   * run twice. The ref leads, state follows for rendering.
   */
  const conversation = useRef<ChatMessage[]>(messages);
  const inFlight = useRef(false);

  const send = useCallback(
    async (text: string): Promise<void> => {
      const trimmed = text.trim();
      if (trimmed === "" || inFlight.current) return;

      inFlight.current = true;
      setIsThinking(true);
      setError(null);

      const history = conversation.current;
      conversation.current = [...history, { role: "user", content: trimmed }];
      setMessages(conversation.current);

      try {
        const reply = await gaiaRepository.sendMessage({ message: trimmed, history, context });
        conversation.current = [...conversation.current, { role: "assistant", content: reply }];
        setMessages(conversation.current);
      } catch (cause: unknown) {
        setError(cause instanceof Error ? cause.message : "Could not reach GAIA.");
      } finally {
        inFlight.current = false;
        setIsThinking(false);
      }
    },
    [context],
  );

  return { messages, isThinking, error, send };
}
